import { atom } from "jotai";

import { BookingWizard } from "../../../types";
import { DurationSelectorAtom } from "../../selectors/durationSelectorAtom/durationSelectorAtom";
import {
  BookingWizardDataAtom,
  currentDurationOptionsAtom,
  ListOfBookingWizardStepsAtom,
  originalListOfBookingWizardStepsAtom,
} from "../../stores";

export const setGroupSizeAtom = atom(null, (get, set, groupSize: number) => {
  const bookingWizardData = get(BookingWizardDataAtom);
  if (!bookingWizardData) {
    //@TODO: this is an error
    return;
  }
  if (bookingWizardData.groupSize === groupSize) {
    return;
  }
  const currentValidDurationOptions = get(currentDurationOptionsAtom);
  const currentDurationSelection = get(DurationSelectorAtom);
  const currentWizardSteps = get(ListOfBookingWizardStepsAtom);
  const originalWizardSteps = get(originalListOfBookingWizardStepsAtom);
  const newBookingWizardData: BookingWizard.BookingWizardData = {
    ...bookingWizardData,
    groupSize,
  };

  /**
   * When we change our group size -- if we have a duration selected that does not accomodate that group size,
   * we clear out our duration and make the user select a new one.
   *
   * We only look at durations matching the current selected course as setting your duration will wipe out your
   * course selection if it doesn't match -- so the issue will eventualy cascade to a solution.
   */
  if (
    newBookingWizardData.type === "DurationBooking" &&
    newBookingWizardData.durationMins
  ) {
    const durationOptionsForCurrentCourse = currentValidDurationOptions.find(
      (duration) => duration.courseId === newBookingWizardData.courseId
    );
    const validDurationsForNewGroupSize =
      durationOptionsForCurrentCourse?.endSlotOptions?.filter((duration) =>
        duration.allowedPlayerCounts?.includes(groupSize)
      );
    if (
      !validDurationsForNewGroupSize?.find(
        (o) => o.durationMins === currentDurationSelection
      )
    ) {
      newBookingWizardData.durationMins = null;
    }
  }

  const isPrivatePlayerStep =
    (bookingWizardData.type === "LessonBooking" ||
      bookingWizardData.type === "RequestBooking") &&
    bookingWizardData.isPrivate;
  const playerStep: BookingWizard.BookingWizardStepTitle = isPrivatePlayerStep
    ? "AddPlayersPrivate"
    : "AddPlayers";
  const isMultiGroupRequest =
    bookingWizardData.type === "RequestBooking" &&
    (bookingWizardData.requestGroupCount ?? 1) > 1;
  const isPrivateRequest =
    bookingWizardData.type === "RequestBooking" && bookingWizardData.isPrivate;
  const shouldKeepPlayerStep = isMultiGroupRequest || isPrivateRequest;

  /**
   * If groupSize is 1, remove player-selection steps unless the request flow still
   * needs them for a private request or multiple request groups. Otherwise,
   * ensure the necessary player-selection step is present.
   */
  if (groupSize === 1) {
    const stepsToRemove: BookingWizard.BookingWizardStepTitle[] =
      shouldKeepPlayerStep
        ? []
        : bookingWizardData.type === "LessonBooking" &&
          bookingWizardData.isPrivate
        ? ["AddPlayers", "AddPlayersPrivate"]
        : [playerStep];

    const filteredSteps = currentWizardSteps.filter(
      (step) => !stepsToRemove.includes(step)
    );
    if (filteredSteps.length !== currentWizardSteps.length) {
      set(ListOfBookingWizardStepsAtom, filteredSteps);
    }
  } else {
    const stepsNeedingAdditionalPlayers: BookingWizard.BookingWizardStepTitle[] =
      [playerStep];

    const nextSteps = [...currentWizardSteps];

    stepsNeedingAdditionalPlayers.forEach((step) => {
      const originalHadStep = originalWizardSteps.includes(step);
      const currentlyHasStep = nextSteps.includes(step);
      if (originalHadStep && !currentlyHasStep) {
        nextSteps.push(step);
      }
    });

    if (
      bookingWizardData.type === "RequestBooking" &&
      groupSize > 1 &&
      !nextSteps.includes(playerStep)
    ) {
      nextSteps.push(playerStep);
    }

    if (nextSteps.length !== currentWizardSteps.length) {
      set(ListOfBookingWizardStepsAtom, nextSteps);
    }
  }
  set(BookingWizardDataAtom, newBookingWizardData);
});
